import { JSONContent } from '@tiptap/core';
import { Fragment, Slice } from '@tiptap/pm/model';
import { NodeViewWrapper } from '@tiptap/react';
import {
  CustomGroupSectionAttributes,
  CustomSectionContentState,
} from 'api/custom-section/types';
import { CustomGroupSectionType } from 'api/custom-section/types/group-section';
import { streamTipTapNodes } from 'helpers/stream';
import { collectRlCustomSectionTemplateSectionIds } from 'helpers/stream/tokens-streaming';
import { ComponentType, useCallback, useState } from 'react';
import { LoaderStreamingComponent } from 'components/common/loader-streaming';
import {
  CustomGroupSectionComponentProps,
  CustomGroupSectionNodeOptions,
  CustomGroupSectionNodeViewProps,
} from './types';
import {
  CustomGroupSectionPrompt,
  CustomGroupSectionGenerated,
  MultiQuestionsGroupSectionPrompt,
} from './ui';

export const CustomGroupSectionNodeView = ({
  node,
  editor,
  updateAttributes,
  getPos,
  extension,
  ...rest
}: CustomGroupSectionNodeViewProps) => {
  const options = extension?.options as
    | CustomGroupSectionNodeOptions
    | undefined;
  const pageType = options?.pageType;
  const resolver = options?.resolver;
  const allowResolve = options?.allowResolve;
  const isPrompt = node.attrs.contentState === CustomSectionContentState.Prompt;
  const isMultiQuestions =
    node.attrs.sectionType === CustomGroupSectionType.MultiQuestions;

  const PromptComponent = isMultiQuestions
    ? MultiQuestionsGroupSectionPrompt
    : CustomGroupSectionPrompt;

  const Component = isPrompt
    ? (PromptComponent as ComponentType<CustomGroupSectionComponentProps>)
    : (CustomGroupSectionGenerated as ComponentType<CustomGroupSectionComponentProps>);

  const replaceContent = useCallback(
    (
      contents: JSONContent[],
      attributes?: Partial<CustomGroupSectionAttributes>,
    ) => {
      const pos = getPos();

      if (pos == null) {
        return;
      }
      const currentNode = editor.state.doc.nodeAt(pos);

      if (!currentNode) {
        return;
      }
      const nodes = contents.map((c) => editor.schema.nodeFromJSON(c));
      const fragment = Fragment.from(nodes);
      const slice = new Slice(fragment, 0, 0);
      const start = pos + 1;
      const end = pos + currentNode.nodeSize - 1;
      const tr = editor.state.tr.replaceRange(start, end, slice);

      if (attributes != null && Object.keys(attributes).length > 0) {
        tr.setNodeMarkup(pos, undefined, {
          ...currentNode.attrs,
          ...attributes,
        });
      }
      editor.view.dispatch(tr);
    },
    [editor, getPos],
  );

  const [isStreaming, setIsStreaming] = useState(false);

  const onResolve = useCallback(
    (
      attributes: CustomGroupSectionAttributes,
      payload?: { content?: JSONContent[] },
    ): Promise<void> =>
      new Promise((resolvePromise) => {
        if (!resolver || !replaceContent) {
          updateAttributes(attributes);
          resolvePromise();

          return;
        }
        resolver(attributes, payload?.content ?? [])
          .then(async (data) => {
            const { streamingMeta, finalContent } = data;

            setIsStreaming(true);
            const nodesToReplace = ((): JSONContent[] => {
              if (finalContent == null) {
                return payload?.content ?? [];
              }
              const asNode = finalContent as JSONContent;

              if (asNode?.type != null) {
                return [asNode];
              }
              if (
                finalContent?.content != null &&
                Array.isArray(finalContent.content) &&
                finalContent.content.length > 0
              ) {
                return finalContent.content as JSONContent[];
              }

              return payload?.content ?? [];
            })();

            const nodePos = getPos();

            if (
              nodePos !== null &&
              nodePos !== undefined &&
              nodesToReplace.length > 0
            ) {
              const contentToInsert = nodesToReplace.map((c) => ({
                ...c,
                attrs: {
                  ...(c.attrs ?? {}),
                  editable: false,
                  isTableStatic: true,
                },
              }));

              const currentNode = editor.state.doc.nodeAt(nodePos);

              if (currentNode) {
                const nodes = contentToInsert.map((c) =>
                  editor.schema.nodeFromJSON(c),
                );
                const fragment = Fragment.from(nodes);
                const tr = editor.state.tr
                  .deleteRange(nodePos, nodePos + currentNode.nodeSize)
                  .insert(nodePos, fragment);

                editor.view.dispatch(tr);
                editor.commands.focus();
              }
            } else if (nodesToReplace.length > 0) {
              replaceContent(nodesToReplace, attributes);
            }

            const idSource =
              nodesToReplace.length > 0
                ? nodesToReplace
                : (payload?.content ?? []);

            const collected = collectRlCustomSectionTemplateSectionIds(
              idSource as Parameters<
                typeof collectRlCustomSectionTemplateSectionIds
              >[0],
            );

            const identifierValues = collected.length > 0 ? collected : ['-1'];

            await new Promise((r) => requestAnimationFrame(r));
            await new Promise((r) => requestAnimationFrame(r));
            await streamTipTapNodes({
              editor,
              identifierType: 'templateSectionId',
              identifierValues,
              token: streamingMeta.token,
              channelName: streamingMeta.channelName,
              shouldFilterByPromptId: true,
              resolve: true,
              onPostStream: () => {
                setIsStreaming(false);
              },
            });
            resolvePromise();
          })
          .catch(() => {
            setIsStreaming(false);
            resolvePromise();
          });
      }),
    [resolver, replaceContent, updateAttributes, editor, getPos],
  );

  const onDone = useCallback(
    (
      attributes: CustomGroupSectionAttributes,
      payload?: { content?: JSONContent[] },
    ) => {
      if (resolver && payload?.content != null) {
        return onResolve(attributes, payload);
      }

      return new Promise<void>((resolve) => {
        updateAttributes(attributes);
        resolve();
      });
    },
    [resolver, onResolve, updateAttributes],
  );

  const onRemove = useCallback(() => {
    const nodePos = getPos();

    if (nodePos !== null && nodePos !== undefined) {
      editor
        .chain()
        .focus()
        .removeCustomGroupSection({ nodeAt: nodePos })
        .run();
    }
  }, [editor, getPos]);

  const handleSectionClick = useCallback(() => {
    const sectionId =
      node.attrs.templateSectionId ?? node.attrs.documentSectionId;
    const sectionMenuConfig = (
      editor.extensionManager.extensions.find(
        (ext) => ext.name === 'sectionMenu',
      ) as any
    )?.options;

    if (sectionMenuConfig?.onSectionClick && sectionId) {
      sectionMenuConfig.onSectionClick({
        id: sectionId,
        ...node.attrs,
        type: node.type.name,
      });
    }
  }, [editor, node.attrs, node.type.name]);

  return (
    <NodeViewWrapper
      contentEditable={!isPrompt && editor.isEditable}
      className={`custom-section-content ${node.attrs.contentState} ${node.attrs.sectionType}`}
      // TODO: have some unique identifier for the node
      key={node.attrs.templateSectionId ?? node.attrs.documentSectionId}
      onClick={handleSectionClick}
    >
      {isStreaming ? (
        <LoaderStreamingComponent />
      ) : (
        <Component
          extension={extension}
          editor={editor}
          node={node}
          getPos={getPos}
          updateAttributes={updateAttributes}
          onDone={onDone}
          onRemove={onRemove}
          replaceContent={isMultiQuestions ? replaceContent : undefined}
          pageType={pageType}
          resolver={isMultiQuestions ? resolver : undefined}
          allowResolve={allowResolve}
          {...rest} // eslint-disable-line react/jsx-props-no-spreading -- forwarding NodeViewProps to section component
        />
      )}
    </NodeViewWrapper>
  );
};
